import {
  faArrowUpFromBracket,
  faBookmark,
  faComment,
  faHeart,
  faPaperPlaneTop,
  faRetweetAlt,
  faShare,
  faThumbsUp,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faArrowLeft,
  faDesktop,
  faEllipsis,
  faMobile,
  faPlus,
  faShare as faShareSolid,
  faWaveformLines,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { format, isToday, sub } from "date-fns";
import { useEffect, useState } from "react";
import { Key, Tab, TabList, Tabs } from "react-aria-components";
import { useFormContext } from "react-hook-form";
import { useMutation } from "react-query";
import { Button, Divider, Loader, Segment } from "semantic-ui-react";
import { richTextEditorDocumentJSONStringToMarkdown } from "../../../../../../components/global_components/form_components/controlled-rich-text-editor/util/controlledRichTextEditorMarkdown";
import { StyledReactMarkdown } from "../../../../../../components/StyledReactMarkdown";
import Tooltip from "../../../../../../components/Tooltip";
import { useClient } from "../../../../../../hooks/useClient";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import useNetworkHeaders from "../../../../../../hooks/useNetworkHeaders";
import { postVoiceNotificationGeneration } from "../../../../../../queries/notify";
import { IconToggleTabBar } from "../../../../../global/components/tab-lists/IconToggleTabBar";
import useSite from "../../../../../permits/components/inspections/InspectionPanel/useSite";
import AppStoreLink from "../../../../components/AppStoreLink";
import GooglePlayStoreLink from "../../../../components/GooglePlayStoreLink";
import TwoWayCommunicationButton from "../../../../components/public_view/TwoWayCommunicationButton";
import { markdownToPlainText } from "../../../../components/public_view_preview";
import { useNotifySettings } from "../../../../hooks/useNotifySettings";
import useNotifySocialMediaPreview from "../../../../hooks/useNotifySocialMediaImagePreview";
import { NotifyNotificationsFormData } from "./NotifyNotificationsForm";

const BreakpointType = {
  Mobile: "mobile",
  Desktop: "desktop",
} as const;
type BreakpointType = (typeof BreakpointType)[keyof typeof BreakpointType];

const PreviewChannelType = {
  Web: "Web",
  Email: "Email",
  SMS: "Sms",
  Voice: "Voice",
  Facebook: "Facebook",
  Twitter: "Twitter",
  Instagram: "Instagram",
} as const;
type PreviewChannelType = (typeof PreviewChannelType)[keyof typeof PreviewChannelType];

const PreviewChannelsSupportingBreakpoints: PreviewChannelType[] = [PreviewChannelType.Web, PreviewChannelType.Email];

export function NotifyNotificationsPreview({
  limitToSelectedChannels = false,
  height,
}: {
  limitToSelectedChannels?: boolean;
  height: string | number;
}) {
  const [breakpoint, setBreakpoint] = useState<BreakpointType>(BreakpointType.Mobile);
  const [previewChannel, setPreviewChannel] = useState<PreviewChannelType>(PreviewChannelType.Web);
  const [enabledChannels, setEnabledChannels] = useState<Set<PreviewChannelType>>(
    new Set([
      PreviewChannelType.Web,
      PreviewChannelType.Email,
      PreviewChannelType.SMS,
      PreviewChannelType.Voice,
      PreviewChannelType.Facebook,
      PreviewChannelType.Twitter,
      PreviewChannelType.Instagram,
    ]),
  );

  const { watch } = useFormContext<NotifyNotificationsFormData>();
  const channels = watch("channels");

  useEffect(() => {
    if (limitToSelectedChannels) {
      const newEnabledChannels = new Set<PreviewChannelType>();
      newEnabledChannels.add(PreviewChannelType.Web); // Always include Web preview
      channels.active.forEach((channel) => {
        if (channel === "Push") return;
        newEnabledChannels.add(channel);
      });
      setEnabledChannels(newEnabledChannels);
    }
  }, [channels]);

  const showBreakpointSelector = PreviewChannelsSupportingBreakpoints.includes(previewChannel);

  return (
    <div
      style={{
        height,
      }}
      className='tw-min-h-0 tw-w-full'
    >
      <div
        className='tw-relative tw-min-h-0'
        style={{
          height,
        }}
      >
        <div
          style={{
            height,
          }}
          className='tw-min-h-0 tw-w-full tw-overflow-auto tw-rounded-md tw-bg-[#EEF1F3] tw-bg-[url(/assets/notify/dot-grid.svg)] tw-bg-repeat'
        >
          <div className='tw-flex tw-flex-col tw-items-center tw-gap-9 tw-p-11'>
            <div>
              <ChannelSelector
                enabledChannels={enabledChannels}
                value={previewChannel}
                onChange={(key) => setPreviewChannel(key as PreviewChannelType)}
              />
            </div>
            <div
              className={classNames("tw-absolute tw-bottom-0 tw-left-0 tw-z-40 tw-m-11 tw-overflow-hidden tw-rounded", {
                "tw-bg-white/60": breakpoint === "mobile",
                "tw-bg-white tw-shadow": breakpoint === "desktop",
              })}
            >
              {showBreakpointSelector && (
                <IconToggleTabBar value={breakpoint} onChange={(key) => setBreakpoint(key as BreakpointType)}>
                  <IconToggleTabBar.UnderlineTab id={BreakpointType.Mobile} icon={faMobile} tooltip='Mobile View' />
                  <IconToggleTabBar.UnderlineTab id={BreakpointType.Desktop} icon={faDesktop} tooltip='Desktop View' />
                </IconToggleTabBar>
              )}
            </div>
            <div
              className={classNames("tw-w-full", {
                "tw-max-w-96": breakpoint === BreakpointType.Mobile && showBreakpointSelector,
                "tw-flex tw-max-w-full tw-justify-center":
                  breakpoint === BreakpointType.Desktop || !showBreakpointSelector,
              })}
            >
              <WebPreview active={previewChannel === PreviewChannelType.Web} />
              <EmailPreview active={previewChannel === PreviewChannelType.Email} />
              <SMSPreview active={previewChannel === PreviewChannelType.SMS} />
              <VoicePreview active={previewChannel === PreviewChannelType.Voice} />
              {previewChannel === PreviewChannelType.Facebook && <FacebookPreview />}
              {previewChannel === PreviewChannelType.Twitter && <TwitterPreview />}
              {previewChannel === PreviewChannelType.Instagram && <InstagramPreview />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function usePreviewData() {
  const site = useSite();
  const { seal, name } = useClient(site);
  const formContext = useFormContext<NotifyNotificationsFormData>();
  if (!formContext) {
    throw new Error("usePreviewData must be used within a FormProvider");
  }

  const { watch } = formContext ?? { watch: () => ({}) };
  const { headline, tagline, body, subscriptionGroups, sendAt, fromDepartment, voiceTranscription, classificationId } =
    watch([
      "headline",
      "tagline",
      "body",
      "subscriptionGroups",
      "sendAt",
      "classificationId",
      "fromDepartment",
      "voiceTranscription",
    ]);

  const publishedAt = sendAt ? sendAt : new Date();
  const publishedAtString = publishedAt
    .toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace("at", "@");
  const bodyMarkdown = richTextEditorDocumentJSONStringToMarkdown(body);

  return {
    seal,
    name,
    headline: headline.trim(),
    tagline: tagline.trim(),
    body: bodyMarkdown,
    subscriptionGroups,
    fromDepartment,
    publishedAt,
    publishedAtString,
    classificationId,
    voiceTranscription,
  };
}

function WebPreview({ active }: { active: boolean }) {
  const {
    headline,
    tagline,
    body,
    subscriptionGroups,
    publishedAtString: publishedAt,
    classificationId,
    fromDepartment,
  } = usePreviewData();

  return (
    <Segment
      className={classNames("tw-w-full tw-space-y-4 tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-lg", {
        "tw-hidden": !active,
      })}
    >
      <div className='tw-flex tw-flex-col tw-gap-2'>
        <div className='tw-mb-3'>
          <Button className='no-padding-button' tabIndex={-1}>
            <FontAwesomeIcon icon={faArrowLeft} className='tw-mr-2' />
            All Posts
          </Button>
        </div>
        {fromDepartment?.label && (
          <h2 className='tw-m-0 tw-mb-3 tw-text-lg tw-font-bold'>From {fromDepartment.label}</h2>
        )}
        {headline && <h1 className='tw-m-0 tw-text-2xl tw-font-bold'>{headline}</h1>}
        {tagline && <h2 className='tw-m-0 tw-text-lg tw-font-bold'>{tagline}</h2>}
        <Divider className='tw-my-2' />
        <div className='tw-text-base tw-font-bold'>Published on {publishedAt}</div>
        {subscriptionGroups && subscriptionGroups.length > 0 && (
          <div className='tw-text-text-secondary'>
            Related Subscription Groups:{" "}
            {subscriptionGroups.map((group, index) => {
              const isLast = index === subscriptionGroups.length - 1;
              return (
                <div key={group.value}>
                  <span className='tw-text-blue'>{group.label}</span>
                  {!isLast && ", "}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Button secondary tabIndex={-1} size='small'>
        <FontAwesomeIcon icon={faShareSolid} className='tw-mr-2' />
        Share
      </Button>
      <Divider />
      <StyledReactMarkdown>{body}</StyledReactMarkdown>
      {classificationId !== null && <TwoWayCommunicationButton tabIndex={-1} />}
    </Segment>
  );
}

function EmailPreview({ active }: { active: boolean }) {
  const {
    seal,
    name,
    headline,
    tagline,
    body,
    subscriptionGroups,
    publishedAtString,
    classificationId,
    fromDepartment,
  } = usePreviewData();

  return (
    <Segment
      className={classNames(
        "tw-m-0 tw-w-full tw-space-y-6 tw-overflow-hidden tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-lg",
        {
          "tw-hidden": !active,
        },
      )}
    >
      <div className='tw-space-y-3'>
        <div className='tw-flex tw-items-center tw-gap-2'>
          {seal && <img src={seal} alt='Agency Seal' className='tw-h-9' />}
          <span className='tw-text-lg'>{name}</span>
        </div>
        {fromDepartment?.label && <h2 className='tw-m-0 tw-text-lg tw-font-bold'>From {fromDepartment.label}</h2>}
        <div className='tw-flex tw-flex-col tw-gap-2'>
          {headline && <h1 className='tw-m-0 tw-text-2xl tw-font-bold'>{headline}</h1>}
          {tagline && <h2 className='tw-m-0 tw-text-lg tw-font-bold'>{tagline}</h2>}
          <Divider className='tw-my-2' />
          <div className='tw-text-base tw-font-bold'>Published on {publishedAtString}</div>
          {subscriptionGroups && subscriptionGroups.length > 0 && (
            <div className='tw-text-text-secondary'>
              Related Subscription Groups:{" "}
              {subscriptionGroups.map((group, index) => {
                const isLast = index === subscriptionGroups.length - 1;
                return (
                  <div key={group.value}>
                    <span className='tw-text-blue'>{group.label}</span>
                    {!isLast && ", "}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <Button secondary size='small'>
          <FontAwesomeIcon icon={faShareSolid} tabIndex={-1} className='tw-mr-2' />
          Share
        </Button>
      </div>
      <Divider />
      <StyledReactMarkdown>{body}</StyledReactMarkdown>
      {classificationId !== null && (
        <div className='tw-flex tw-w-full tw-justify-center'>
          <TwoWayCommunicationButton tabIndex={-1} />
        </div>
      )}
      <Divider />
      <div className='tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-9'>
        <Button className='no-padding-button tw-m-0 tw-text-text-secondary' tabIndex={-1}>
          Manage Subscription
        </Button>
        <img src='/assets/powered-by-gogov.png' className='tw-w-32' alt='Powered by GoGov' />
        <div className='tw-flex tw-gap-2'>
          <AppStoreLink className='tw-pointer-events-none tw-h-[32px]'></AppStoreLink>
          <GooglePlayStoreLink className='tw-pointer-events-none tw-h-[32px]'></GooglePlayStoreLink>
        </div>
      </div>
      <div>
        <div className='-tw-mx-4 -tw-mb-4 tw-h-2 tw-bg-[#264289]'></div>
      </div>
    </Segment>
  );
}

export function stripNonGSM7Characters(input: string): string {
  const isGSM7CompliantRegex =
    /^[A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!"#$%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|\u20AC]*$/;
  let result = "";
  for (const char of input) {
    if (isGSM7CompliantRegex.test(char)) {
      result += char;
    }
  }
  return result;
}

function SMSPreview({ active }: { active: boolean }) {
  const { headline, tagline, publishedAt } = usePreviewData();

  const { notifySettings, isLoading } = useNotifySettings({
    enabled: active,
    refetchOnMount: true,
  });

  const smsTimestamp = (date?: Date | null) => {
    const actualDate = !!date ? date : new Date();
    const actualDateIsToday = isToday(actualDate);
    if (actualDateIsToday) return format(actualDate, "'Today' h:mm a");
    return format(actualDate, "E, LLL d 'at' h:mm a"); // e.g. Thu, Aug 15 at 11:55 AM
  };

  const smsNotificationPreview = () => {
    const maxLength = 160;

    if (!notifySettings) return "Loading...";

    const { smsPreviewNotificationMessage } = notifySettings;

    const body = `${stripNonGSM7Characters(headline)}\n${stripNonGSM7Characters(tagline)}`.trim();
    const bodyIsEmpty = body.length === 0;
    if (bodyIsEmpty) return smsPreviewNotificationMessage.replace("\n{{BODY}}", "");

    const lengthWithoutBody = smsPreviewNotificationMessage.replace("{{BODY}}", "").length;
    const maxBodyLength = maxLength - lengthWithoutBody;
    const shouldTrim = body.length > maxBodyLength;

    const trimmedBody = shouldTrim ? `${body.substring(0, maxBodyLength - 3)}...` : body;

    return smsPreviewNotificationMessage.replace("{{BODY}}", trimmedBody);
  };

  return (
    <Segment
      className={classNames(
        "tw-m-0 tw-h-[603px] tw-w-96 tw-overflow-hidden tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-lg",
        {
          "tw-hidden": !active,
        },
      )}
    >
      <Loader active={isLoading} />
      {notifySettings && (
        <SMSConversationPreview>
          <SMSConversationPreview.Fade />
          <SMSConversationPreview.SMSSenderMessage
            timestamp={smsTimestamp(sub(publishedAt ?? new Date(), { days: 1, hours: 3, minutes: 6, seconds: 10 }))}
          >
            {notifySettings?.smsPreviewInviteMessage ?? "Loading..."}
          </SMSConversationPreview.SMSSenderMessage>
          <SMSConversationPreview.SMSRecipientMessage>JOIN</SMSConversationPreview.SMSRecipientMessage>
          <SMSConversationPreview.SMSSenderMessage>
            {notifySettings?.smsPreviewWelcomeMessage ?? "Loading..."}
          </SMSConversationPreview.SMSSenderMessage>
          <SMSConversationPreview.SMSSenderMessage highlight timestamp={smsTimestamp(publishedAt)}>
            {smsNotificationPreview()}
          </SMSConversationPreview.SMSSenderMessage>
        </SMSConversationPreview>
      )}
    </Segment>
  );
}

function VoicePreview({ active }: { active: boolean }) {
  const networkHeaders = useNetworkHeaders();
  const { voiceTranscription } = usePreviewData();

  const [previouslyGeneratedVoiceContent, setPreviouslyGeneratedVoiceContent] = useState<string | undefined>();
  const [generateVoicePreviewMutation, { isLoading: isGenerateVoicePreviewMutationLoading, data: voicePreview }] =
    useMutation(postVoiceNotificationGeneration(networkHeaders));

  const voiceContentHasChanged = voiceTranscription?.trim() !== previouslyGeneratedVoiceContent?.trim();
  const voiceContentIsEmpty = !voiceTranscription || voiceTranscription.trim().length === 0;
  const previouslyGeneratedVoiceContentIsEmpty =
    !previouslyGeneratedVoiceContent || previouslyGeneratedVoiceContent.trim().length === 0;
  const showEmptyVoiceTranscriptionWarning =
    !isGenerateVoicePreviewMutationLoading && voiceContentIsEmpty && previouslyGeneratedVoiceContentIsEmpty;

  return (
    <Segment
      className={classNames("tw-m-0 tw-w-full tw-overflow-hidden tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-lg", {
        "tw-hidden": !active,
      })}
    >
      <div className='tw-flex tw-items-center tw-gap-1'>
        <Button
          className='tw-m-0'
          onClick={async () => {
            if (!voiceTranscription) return;
            setPreviouslyGeneratedVoiceContent(voiceTranscription);
            await generateVoicePreviewMutation({ voiceContent: voiceTranscription, isPreview: true });
          }}
          loading={isGenerateVoicePreviewMutationLoading}
          disabled={isGenerateVoicePreviewMutationLoading || !voiceTranscription || !voiceContentHasChanged}
          icon={<FontAwesomeIcon icon={faWaveformLines} className='tw-mr-2' />}
          content='Generate Preview'
        />
        {showEmptyVoiceTranscriptionWarning && (
          <Tooltip>Voice transcription is empty. Please enter text to generate a voice preview.</Tooltip>
        )}
      </div>
      {isGenerateVoicePreviewMutationLoading && (
        <>
          <Divider className='-tw-mx-6 tw-my-6' />
          <div>Generating... For long messages, this might take a while</div>
        </>
      )}
      {voicePreview && (
        <>
          <Divider className='-tw-mx-6 tw-my-6' />
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls src={voicePreview.url}>
            Your browser does not support the audio element.
            <a href={voicePreview.url} download>
              Download Audio
            </a>
          </audio>
          <div className='tw-mt-3 tw-whitespace-pre-wrap'>{voicePreview.transcript}</div>
        </>
      )}
    </Segment>
  );
}

class PostBuilder {
  private totalStaticLength = 0;

  constructor(private maxLength: number) {}

  private addEllipsis(text: string, maxLength: number) {
    if (maxLength <= 3) return text; // Not enough space for ellipsis
    const shouldAddEllipsis = text.length > maxLength;
    return shouldAddEllipsis ? `${text.slice(0, maxLength - 3)}...` : text;
  }

  private addPunctuation(text: string) {
    const trimmedText = text.trim();
    if (trimmedText.length === 0) return text;
    const punctuation = [".", "?", "!"];
    const lastChar = text.slice(-1);
    if (punctuation.includes(lastChar)) return text;
    return `${text}.`;
  }

  addStaticText(text: string) {
    this.totalStaticLength += this.addPunctuation(text.trim()).length;
    return this;
  }

  addWhitespace(text: string = " ") {
    this.totalStaticLength += text.length;
    return this;
  }

  build(text: string) {
    const availableLength = this.maxLength - this.totalStaticLength;
    return this.addEllipsis(this.addPunctuation(text.trim()), availableLength);
  }
}

function FacebookPreview() {
  const previewData = usePreviewData();
  const { name, headline, tagline, body, seal, publishedAt } = useDebounce(previewData, 250);
  const { canvasRef } = useNotifySocialMediaPreview({
    headline,
    tagline,
    content: body,
    citySeal: seal ?? "",
  });

  const publishedAtString = publishedAt
    ? publishedAt.toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const builder = new PostBuilder(63206)
    .addStaticText(headline)
    .addWhitespace("\n\n")
    .addStaticText(tagline)
    .addWhitespace("\n\n")
    // body
    .addWhitespace("\n\n")
    .addStaticText("Read More: govoutreach.com/vertucciindustries/...");

  const formattedBody = builder.build(markdownToPlainText(body));

  return (
    <Segment className='tw-m-0 tw-flex tw-w-[450px] tw-flex-col tw-gap-3 tw-overflow-hidden tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-lg'>
      <div className='tw-flex tw-justify-between'>
        <div className='tw-flex tw-items-center tw-gap-2'>
          {seal && <img src={seal} alt='Agency Seal' className='tw-h-9' />}
          <div>
            <h2 className='tw-m-0 tw-text-base tw-font-bold'>{name}</h2>
            <div className='-tw-mt-1 tw-text-sm tw-font-bold tw-text-text-secondary'>{publishedAtString}</div>
          </div>
        </div>
        <div>
          <FontAwesomeIcon icon={faEllipsis} className='tw-m-2 tw-h-5 tw-w-5 tw-text-text-secondary' />
          <FontAwesomeIcon icon={faXmark} className='tw-m-2 tw-h-5 tw-w-5 tw-text-text-secondary' />
        </div>
      </div>
      <div>
        <div>{headline}</div>
        <br />
        <div>{tagline}</div>
        <br />
        <div>{formattedBody}</div>
        <br />
        <div>
          Read More: <span className='tw-text-blue'>govoutreach.com/vertucciindustries/...</span>
        </div>
      </div>
      <div className='-tw-mx-4'>
        <canvas
          ref={canvasRef}
          width={900}
          height={900}
          className='tw-w-full tw-border-x-0 tw-border-y-[1px] tw-border-solid tw-border-border'
        />
      </div>
      <div className='-tw-my-1 tw-flex tw-justify-between'>
        <span className='tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-font-bold tw-text-text-hint'>
          <FontAwesomeIcon icon={faThumbsUp} className='tw-h-6 tw-w-6' />
          Like
        </span>
        <span className='tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-font-bold tw-text-text-hint'>
          <FontAwesomeIcon icon={faComment} className='tw-h-6 tw-w-6' />
          Comment
        </span>
        <span className='tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-font-bold tw-text-text-hint'>
          <FontAwesomeIcon icon={faShare} className='tw-h-6 tw-w-6' />
          Share
        </span>
      </div>
    </Segment>
  );
}

function TwitterPreview() {
  const previewData = usePreviewData();
  const { name, headline, tagline, body, seal, publishedAt } = useDebounce(previewData, 250);
  const { canvasRef } = useNotifySocialMediaPreview({
    headline,
    tagline,
    content: body,
    citySeal: seal ?? "",
  });

  const publishedAtString = publishedAt
    ? publishedAt.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";

  const builder = new PostBuilder(280)
    .addStaticText(headline)
    .addWhitespace("\n\n")
    .addStaticText(tagline)
    .addWhitespace("\n\n")
    // body
    .addWhitespace("\n\n")
    .addStaticText("Read More: govoutreach.com/vertucciindustries/...");

  const formattedBody = builder.build(markdownToPlainText(body));

  return (
    <Segment className='tw-m-0 tw-flex tw-w-[450px] tw-gap-3 tw-overflow-hidden tw-rounded-lg tw-bg-white tw-p-6 tw-shadow-lg'>
      {seal ? (
        <img src={seal} alt='Agency Seal' className='tw-h-[40px] tw-rounded-full' />
      ) : (
        <div className='tw-h-[40px] tw-w-[40px] tw-flex-shrink-0 tw-rounded-full tw-bg-white-mid' />
      )}
      <div className='tw-flex tw-flex-col tw-gap-3'>
        <div>
          <div className='tw-flex tw-items-center tw-justify-between'>
            <div className='tw-flex tw-items-baseline tw-gap-1'>
              <h2 className='tw-m-0 tw-text-base tw-font-bold'>{name}</h2>
              <span className='-tw-mt-1 tw-text-base tw-text-text-hint'>@placeholder</span>
              <span className='-tw-mt-1 tw-text-base tw-text-text-hint'>·</span>
              <span className='-tw-mt-1 tw-text-base tw-text-text-hint'>{publishedAtString}</span>
            </div>
            <FontAwesomeIcon icon={faEllipsis} className='tw-text-text-secondary' />
          </div>
          <div>
            <div>{headline}</div>
            <br />
            <div>{tagline}</div>
            <br />
            <div>{formattedBody}</div>
            <br />
            <div>
              Read More: <span className='tw-text-blue'>govoutreach.com/vertucciindustries/...</span>
            </div>
          </div>
        </div>
        <canvas
          ref={canvasRef}
          width={900}
          height={900}
          className='tw-w-full tw-rounded-2xl tw-border tw-border-solid tw-border-[#CFD9DE]'
        />
        <div className='-tw-mb-3 tw-flex tw-items-center tw-justify-between'>
          <span className='tw-text-text-hint'>
            <FontAwesomeIcon icon={faComment} className='tw-h-[19px] tw-w-[19px]' />
          </span>
          <span className='tw-text-text-hint'>
            <FontAwesomeIcon icon={faRetweetAlt} className='tw-h-[26px] tw-w-[26px]' />
          </span>
          <span className='tw-text-text-hint'>
            <FontAwesomeIcon icon={faHeart} className='tw-h-[19px] tw-w-[19px]' />
          </span>
          <span className='tw-text-text-hint'>
            <FontAwesomeIcon icon={faArrowUpFromBracket} className='tw-h-[18px] tw-w-[18px]' />
          </span>
        </div>
      </div>
    </Segment>
  );
}

function InstagramPreview() {
  const previewData = usePreviewData();
  const { name, headline, tagline, body, seal } = useDebounce(previewData, 250);
  const { canvasRef } = useNotifySocialMediaPreview({
    headline,
    tagline,
    content: body,
    citySeal: seal ?? "",
  });

  const builder = new PostBuilder(2200)
    .addStaticText(headline)
    .addWhitespace("\n\n")
    .addStaticText(tagline)
    .addWhitespace("\n\n")
    // body
    .addWhitespace("\n\n")
    .addStaticText("Read More: govoutreach.com/vertucciindustries/...");

  const formattedBody = builder.build(markdownToPlainText(body));

  return (
    <Segment className='tw-m-0 tw-flex tw-w-[450px] tw-flex-col tw-gap-3 tw-overflow-hidden tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-lg'>
      <div className='tw-flex tw-justify-between'>
        <div className='tw-flex tw-items-center tw-gap-1'>
          {seal ? (
            <img src={seal} alt='Agency Seal' className='tw-h-9 tw-rounded-full' />
          ) : (
            <div className='tw-h-9 tw-w-9 tw-flex-shrink-0 tw-rounded-full tw-bg-white-mid' />
          )}
          <div className='tw-ml-2'>
            <h2 className='tw-m-0 tw-text-base tw-font-bold'>{name}</h2>
          </div>
        </div>
        <div className='tw-flex tw-items-center tw-gap-1'>
          <Button className='tw-cursor-default tw-rounded-xl tw-border-none tw-bg-[#EFEFEF] tw-text-text' tabIndex={-1}>
            Follow
          </Button>
          <FontAwesomeIcon icon={faEllipsis} className='tw-m-2 tw-h-5 tw-w-5 tw-text-text' />
        </div>
      </div>
      <canvas
        ref={canvasRef}
        width={900}
        height={900}
        className='tw-w-full tw-rounded-md tw-border tw-border-solid tw-border-border'
      />
      <div className='tw-mt-1'>
        <FontAwesomeIcon icon={faHeart} className='tw-mr-3 tw-h-7 tw-w-7 tw-text-text' />
        <FontAwesomeIcon icon={faComment} className='tw-mr-3 tw-h-7 tw-w-7 tw-text-text' />
        <FontAwesomeIcon icon={faPaperPlaneTop} className='tw-h-7 tw-w-7 tw-text-text' />
        <FontAwesomeIcon icon={faBookmark} className='tw-float-right tw-h-7 tw-w-7 tw-text-text' />
      </div>
      <div>
        <div className='tw-mb-1 tw-text-lg tw-font-bold'>1,234 likes</div>
        <div>
          <span className='tw-font-bold'>{name}</span> {headline}
        </div>
        <br />
        <div>{tagline}</div>
        <br />
        <div>{formattedBody}</div>
      </div>
    </Segment>
  );
}

function SMSConversationPreview({ children }: React.PropsWithChildren<{}>) {
  return (
    <div className='tw-flex tw-w-full tw-max-w-sm tw-flex-col tw-gap-2'>
      {children}
      <div className='tw-mt-3 tw-flex  tw-items-center tw-gap-3'>
        <div className='tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#ebecf0]'>
          <FontAwesomeIcon icon={faPlus} className='tw-mx-auto tw-text-grey' />
        </div>
        <div className='tw-flex tw-h-8 tw-w-full tw-select-none tw-items-center tw-rounded-full tw-p-3 tw-text-grey-light tw-ring-1 tw-ring-grey-light'>
          Text Message
        </div>
      </div>
    </div>
  );
}

SMSConversationPreview.Fade = () => {
  return (
    <div className='tw-pointer-events-none tw-absolute tw-bottom-2/3 tw-left-0 tw-right-0 tw-top-0 tw-z-10 tw-rounded-t-lg tw-bg-gradient-to-b tw-from-white tw-to-transparent'></div>
  );
};

SMSConversationPreview.SMSSenderMessage = ({
  children,
  timestamp,
  highlight = false,
}: React.PropsWithChildren<{ timestamp?: string; highlight?: boolean }>) => {
  return (
    <div className={classNames({ "tw-mt-3 tw-flex tw-flex-col tw-gap-2": timestamp })}>
      <div className='tw-flex tw-w-full tw-justify-center tw-text-sm tw-text-grey'>{timestamp}</div>
      <div className='tw-mr-16 tw-flex'>
        <div
          className={classNames({
            "tw-whitespace-pre-line tw-rounded-3xl tw-rounded-es-lg tw-bg-[#ebecf0] tw-px-5 tw-py-3 tw-text-black": true,
            "tw-shadow-lg tw-ring-2 tw-ring-[#0E85FE]": highlight,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

SMSConversationPreview.SMSRecipientMessage = ({ children }: React.PropsWithChildren<{}>) => {
  return (
    <div className='tw-flex tw-w-full tw-justify-end'>
      <div className='tw-whitespace-pre-line tw-rounded-3xl tw-rounded-ee-lg tw-bg-[#0E85FE] tw-px-5 tw-py-2 tw-text-white'>
        {children}
      </div>
    </div>
  );
};

function ChannelSelector({
  enabledChannels,
  defaultValue,
  value,
  onChange,
}: {
  enabledChannels: Set<PreviewChannelType>;
  defaultValue?: Key;
  value?: Key;
  onChange?: (key: Key) => void;
}) {
  return (
    <Segment className='tw-m-0 tw-w-full tw-p-1 xl:tw-w-auto'>
      <Tabs orientation='horizontal' defaultSelectedKey={defaultValue} onSelectionChange={onChange} selectedKey={value}>
        <TabList className='tw-flex tw-gap-1'>
          {/* Always show web channel */}
          <ChannelSelectorTab id='Web'>Web</ChannelSelectorTab>
          {enabledChannels.has("Email") && <ChannelSelectorTab id='Email'>Email</ChannelSelectorTab>}
          {enabledChannels.has("Sms") && <ChannelSelectorTab id='Sms'>SMS</ChannelSelectorTab>}
          {enabledChannels.has("Voice") && <ChannelSelectorTab id='Voice'>Voice</ChannelSelectorTab>}
          {enabledChannels.has("Facebook") && <ChannelSelectorTab id='Facebook'>Facebook</ChannelSelectorTab>}
          {enabledChannels.has("Twitter") && <ChannelSelectorTab id='Twitter'>Twitter</ChannelSelectorTab>}
          {enabledChannels.has("Instagram") && <ChannelSelectorTab id='Instagram'>Instagram</ChannelSelectorTab>}
        </TabList>
      </Tabs>
    </Segment>
  );
}

function ChannelSelectorTab({
  children,
  id,
}: React.PropsWithChildren<{
  id: Key;
}>) {
  return (
    <Tab id={id}>
      {({ isSelected }) => (
        <div
          className={`tw-cursor-pointer tw-rounded-md tw-px-3 tw-py-2 tw-text-base tw-transition-colors tw-duration-75 ${
            isSelected ? "tw-bg-white-dark tw-font-bold" : "hover:tw-bg-white-off active:tw-bg-[#00000023]"
          }`}
        >
          {children}
        </div>
      )}
    </Tab>
  );
}
